import { Slot } from "@radix-ui/react-slot";
import { LinkComponent as RouterLinkComponent, createLink, useRouter } from "@tanstack/react-router";
import { AnchorHTMLAttributes, ComponentProps, MouseEvent, forwardRef } from "react";

import { cn } from "@/lib/utils";
import { isPreviousHistoryEntry } from "@/utils/navigation-history";

type InternalLinkProps = {
    asChild?: boolean;
    external?: false;
};

type ExternalLinkProps = {
    asChild?: boolean;
    external: true;
};

type LinkProps = (InternalLinkProps | ExternalLinkProps) & AnchorHTMLAttributes<HTMLAnchorElement>;

const LinkComponent = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ className, asChild = false, external, ...props }, ref) => {
        if (external) {
            const Comp = asChild ? Slot : "a";

            return <Comp ref={ref} className={cn(className)} target="_blank" rel="noopener noreferrer" {...props} />;
        }

        if (asChild) {
            return <Slot ref={ref} className={cn(className)} {...props} />;
        }

        return <a ref={ref} className={cn(className)} {...props} />;
    }
);

export const CreatedLinkComponent = createLink(LinkComponent);

type CreatedLinkProps = ComponentProps<typeof CreatedLinkComponent>;

const isPlainLeftClick = (e: MouseEvent<HTMLAnchorElement>) =>
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const LinkWithRef = forwardRef<HTMLAnchorElement, CreatedLinkProps>(function Link({ onClick, ...props }, ref) {
    const router = useRouter();

    // When the link points to the page we just came from, go back instead of pushing it again,
    // so that page keeps the search params (filters, pagination, ...) the user had selected.
    // Links that set their own `search`/`hash` keep their explicit target.
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);

        if (
            e.defaultPrevented ||
            !isPlainLeftClick(e) ||
            props.external ||
            props.disabled ||
            (props.target && props.target !== "_self") ||
            props.search !== undefined ||
            props.hash !== undefined ||
            !props.to
        ) {
            return;
        }

        const { pathname } = router.buildLocation({
            to: props.to,
            params: props.params,
            from: props.from,
        } as Parameters<typeof router.buildLocation>[0]);

        if (isPreviousHistoryEntry(router.history, pathname)) {
            e.preventDefault();
            router.history.back();
        }
    };

    return <CreatedLinkComponent ref={ref} preload="intent" {...props} onClick={handleClick} />;
});

LinkWithRef.displayName = "Link";

export const Link = LinkWithRef as RouterLinkComponent<typeof LinkComponent>;
